import { Application, Container, FillGradient, Graphics, Sprite, Text } from 'pixi.js'
import type { Texture } from 'pixi.js'

import { playReelStop } from '../audio/sfx'
import { WAYS_TILE, boardLayout, tileFor } from '../config'
import type { BoardLayout } from '../config'
import {
  BACKGROUND_KEY,
  DEFAULT_LINES_TIMING,
  DEFAULT_WAYS_TIMING,
  FRAME_KEY,
  animationFor,
  backgroundTextureFor,
  loadThemeAssets,
  textureFor,
} from '../theme'
import type { ResolvedTheme, WaysTiming } from '../theme'
import { coverBoard, fillCell, fitArt, fitFrame } from './art'
import { driveWhileHidden } from './backgroundClock'
import { PixiLines } from './PixiLines'
import type { LineWinView } from './PixiLines'
import { generatedPalette, styleFrom } from './symbols'
import type { GameSymbol, SymbolStyle } from './symbols'

/**
 * The game board on PixiJS.
 *
 * This is an imperative layer, and it deliberately knows nothing about
 * React. React mounts the canvas once and never touches it again: if the
 * reels lived in the component tree, every setState during a spin would
 * jolt the render exactly when 60 fps has to be held.
 *
 * The exchange is one-way: React calls the methods of this class, the
 * class returns promises. The game state stays in the store, not here.
 *
 * Grid geometry comes from the game description rather than constants:
 * one lobby may hold several ways games of different sizes. Look — palette,
 * sprites, font, timings — comes from the theme, so a new skin needs no
 * edit here (see `docs/theme-pipeline.md`).
 */

/** Win fire geometry is rebuilt at this rate, independent of the ticker. */
const FIRE_FRAME_MS = 1000 / 30

/** What the board needs to know about a game: how many cells, which symbols. */
export interface WaysGameView {
  gameId: string
  layout: { reels: number; rows: number }
  symbols: GameSymbol[]
}

interface Tile {
  view: Container
  bg: Graphics
  /** The symbol's own backdrop art, under `art`. Hidden without one. */
  bgArt: Sprite
  /** Symbol art. Empty until a theme supplies a frame for this code. */
  art: Sprite
  label: Text
  code: string
  /** Flipbook state, when the theme animates this symbol. */
  anim: { textures: Texture[]; fps: number; time: number; index: number } | null
}

/** A smooth stop with a slight overshoot — that is what makes a reel read "heavy". */
function easeOutBack(p: number): number {
  const c1 = 1.70158
  const c3 = c1 + 1
  return 1 + c3 * (p - 1) ** 3 + c1 * (p - 1) ** 2
}

/** Shared by every reel: geometry, styling, random symbol source. */
interface ReelContext {
  L: BoardLayout
  styleOf: (code: string) => SymbolStyle
  /** Art frame for a symbol, or null — then a captioned tile. */
  textureOf: (code: string) => Texture | null
  /** The symbol's own backdrop, or null — then the flat `fill` tile. */
  backgroundOf: (code: string) => Texture | null
  /** Flipbook for a symbol, or null — then the art stays still. */
  animationOf: (code: string) => { textures: Texture[]; fps: number } | null
  randomCode: () => string
  /**
   * A reel just locked in. Fired from the reel itself rather than
   * scheduled from `spinTo`, because the stop lands on the next symbol
   * boundary after the deadline — up to a whole pitch later. A sound
   * timed from the formula would drift audibly off the visible clunk.
   */
  onReelStop: (index: number) => void
  T: WaysTiming
  font: { family: string; weight: string; letterSpacing: number }
  tileRadius: number
  /** Present only when the game is themed — art sizing needs it. */
  theme: ResolvedTheme | null
}

class Reel {
  readonly view = new Container()
  private readonly tiles: Tile[] = []
  private readonly codes: string[] = []
  private readonly ctx: ReelContext
  private readonly index: number

  private offset = 0
  private state: 'idle' | 'spinning' | 'settling' = 'idle'
  private injectQueue: string[] = []
  private settleFrom = 0
  private settleT = 0
  private startDelay = 0
  private stopAfter = 0
  private elapsed = 0
  /** Playback rate for this spin: >1 scrolls and settles proportionally faster. */
  private speed = 1

  constructor(index: number, ctx: ReelContext) {
    this.ctx = ctx
    this.index = index
    this.view.x = index * ctx.L.pitch

    for (let i = 0; i < ctx.L.tilesPerReel; i++) {
      const code = ctx.randomCode()
      this.codes.push(code)
      this.tiles.push(this.createTile(code))
      this.view.addChild(this.tiles[i].view)
    }
    this.layout()
  }

  private createTile(code: string): Tile {
    const { L, font } = this.ctx
    const style = this.ctx.styleOf(code)
    const view = new Container()

    const bg = new Graphics()
    // Snap the art layers to whole device pixels — see the same call in
    // PixiTumble. The canvas is shown at a size the layout picks, so the
    // logical-to-device factor is fractional and every reel lands on its
    // own subpixel phase; without this, identical symbols in one spin
    // sample the texture differently and read as uneven quality.
    const bgArt = new Sprite()
    bgArt.anchor.set(0.5)
    bgArt.roundPixels = true
    bgArt.x = L.symbolSize / 2
    bgArt.y = L.symbolSize / 2
    bgArt.visible = false
    const art = new Sprite()
    art.anchor.set(0.5)
    art.roundPixels = true
    art.x = L.symbolSize / 2
    art.y = L.symbolSize / 2
    art.width = L.symbolSize
    art.height = L.symbolSize
    art.visible = false
    const label = new Text({
      text: style.label,
      style: {
        fontFamily: font.family,
        fontSize: style.fontSize,
        fontWeight: font.weight as '800',
        fill: style.text,
        letterSpacing: font.letterSpacing,
      },
    })
    label.anchor.set(0.5)
    label.x = L.symbolSize / 2
    label.y = L.symbolSize / 2

    // Order is the layer order: flat fill → symbol backdrop → character →
    // label. Both art layers live in the same tile container, so they move,
    // fall and vanish together by construction.
    view.addChild(bg, bgArt, art, label)
    const tile: Tile = { view, bg, bgArt, art, label, code, anim: null }
    this.paint(tile, code)
    return tile
  }

  private paint(tile: Tile, code: string): void {
    const { L } = this.ctx
    const style = this.ctx.styleOf(code)
    tile.code = code
    tile.bg.clear()
    // A repaint may land while a win animation is mid-flight — reset.
    tile.art.rotation = 0

    // Layer 1 — the symbol's own backdrop, stretched to the whole cell.
    // Without one (or when its file failed to load) the flat fill stands in.
    const bgTexture = this.ctx.backgroundOf(code)
    tile.bgArt.visible = bgTexture !== null
    if (bgTexture) {
      tile.bgArt.texture = bgTexture
      fillCell(tile.bgArt, L.symbolSize)
    }

    // Layer 2 — the character. Symbols without art stay placeholders: a
    // half-drawn theme must remain playable, and a themed backdrop must not
    // wait for its character to be worth showing.
    const texture = this.ctx.textureOf(code)
    if (texture) {
      tile.art.texture = texture
      tile.art.visible = true
      if (this.ctx.theme) fitArt(tile.art, texture, L.symbolSize, this.ctx.theme, style.scale)
      tile.label.visible = false
      // The flipbook restarts on repaint. Frames share the base image's
      // dimensions, so swapping textures keeps the fitted size.
      const anim = this.ctx.animationOf(code)
      tile.anim = anim ? { ...anim, time: 0, index: 0 } : null
    } else {
      tile.anim = null
      tile.art.visible = false
      tile.label.visible = true
      tile.label.text = style.label
      tile.label.style.fill = style.text
      tile.label.style.fontSize = style.fontSize
    }

    // The fill is a fallback for the backdrop, not a base under it: themes
    // whose art is drawn on transparency would grow a coloured square.
    if (!bgTexture && (!texture || style.backgroundImage)) {
      tile.bg.roundRect(0, 0, L.symbolSize, L.symbolSize, this.ctx.tileRadius).fill(style.fill)
    }
  }

  /** Lays the tiles out at the current offset. Tile 0 is the buffer, above the window. */
  private layout(): void {
    for (let i = 0; i < this.tiles.length; i++) {
      this.tiles[i].view.y = (i - 1) * this.ctx.L.pitch + this.offset
    }
  }

  private applyCodes(): void {
    for (let i = 0; i < this.tiles.length; i++) {
      if (this.tiles[i].code !== this.codes[i]) {
        this.paint(this.tiles[i], this.codes[i])
      }
    }
  }

  /** Show a window instantly, with no animation. */
  setWindow(window: string[]): void {
    const { L, randomCode } = this.ctx
    this.offset = 0
    this.state = 'idle'
    this.injectQueue = []
    for (let row = 0; row < L.rows; row++) {
      this.codes[row + 1] = window[row]
    }
    this.codes[0] = randomCode()
    for (let i = L.rows + 1; i < this.codes.length; i++) {
      this.codes[i] = randomCode()
    }
    this.applyCodes()
    this.layout()
  }

  start(startDelay: number, stopAfter: number, speed = 1): void {
    this.state = 'spinning'
    this.startDelay = startDelay
    this.stopAfter = stopAfter
    this.speed = speed
    this.elapsed = 0
    this.injectQueue = []
  }

  /**
   * Queue that injects the final symbols.
   *
   * New symbols enter from the top (index 0) and shift one position down
   * with every turn. For final[0..rows-1] to end up in the visible rows
   * 1..rows they have to be injected in reverse order, followed by one
   * more filler that will occupy the buffer tile at the top.
   */
  private queueFinal(window: string[]): void {
    this.injectQueue = [...window].reverse().concat(this.ctx.randomCode())
  }

  get isIdle(): boolean {
    return this.state === 'idle'
  }

  /**
   * Advance the flipbooks. Runs on every frame, spin or no spin — the
   * flames on a seven must not freeze the moment the reels go quiet.
   */
  tickAnimations(dtMs: number): void {
    for (const tile of this.tiles) {
      const anim = tile.anim
      if (!anim || !tile.art.visible) continue
      anim.time += dtMs
      const index = Math.floor((anim.time * anim.fps) / 1000) % anim.textures.length
      if (index !== anim.index) {
        anim.index = index
        tile.art.texture = anim.textures[index]
      }
    }
  }

  /**
   * Art sprites of a visible row, for win animations: backdrop and
   * character, whichever are drawn. Empty when the cell is a captioned
   * placeholder — that one does not swell.
   *
   * Both layers are returned so the highlight applies to the whole icon:
   * a character swelling off its own backdrop reads as a broken sprite.
   */
  artAt(row: number): Sprite[] {
    const tile = this.tiles[row + 1]
    if (!tile) return []
    return [tile.bgArt, tile.art].filter((s) => s.visible)
  }

  update(dtMs: number, final: string[]): void {
    if (this.state === 'idle') return

    this.elapsed += dtMs
    if (this.elapsed < this.startDelay) return

    if (this.state === 'settling') {
      this.settleT += dtMs
      const p = Math.min(1, (this.settleT * this.speed) / this.ctx.T.settleMs)
      this.offset = this.settleFrom * (1 - easeOutBack(p))
      this.layout()
      if (p >= 1) {
        this.offset = 0
        this.state = 'idle'
        this.layout()
      }
      return
    }

    this.offset += (this.ctx.T.scrollSpeed * this.speed * dtMs) / 1000

    while (this.offset >= this.ctx.L.pitch) {
      this.offset -= this.ctx.L.pitch

      const shouldStop = this.elapsed - this.startDelay >= this.stopAfter
      if (shouldStop && this.injectQueue.length === 0) {
        this.queueFinal(final)
      }

      const next =
        this.injectQueue.length > 0 ? this.injectQueue.shift()! : this.ctx.randomCode()
      this.codes.pop()
      this.codes.unshift(next)
      this.applyCodes()

      if (shouldStop && this.injectQueue.length === 0) {
        this.state = 'settling'
        this.settleFrom = this.offset
        this.settleT = 0
        this.ctx.onReelStop(this.index)
        break
      }
    }

    this.layout()
  }
}

/** Board-sized art layer (backdrop or frame), if the theme ships one. */
function addArtLayer(
  stage: Container,
  theme: ResolvedTheme | undefined,
  key: string,
  L: BoardLayout,
): void {
  // Inset frame art is rendered as DOM overscan around the board. The Pixi
  // canvas remains exactly the grid size so reel masks and input do not move.
  if (key === FRAME_KEY && theme?.board.frameInset) return
  const texture = theme ? textureFor(theme, key) : null
  if (!texture) return
  const sprite = new Sprite(texture)
  sprite.anchor.set(0.5)
  // Only the frame carries an inset: a backdrop lives under the cells and
  // has nothing to line up with.
  if (key === FRAME_KEY) {
    fitFrame(sprite, texture, L.boardWidth, L.boardHeight, theme?.board.frameInset ?? null)
  } else {
    coverBoard(sprite, texture, L.boardWidth, L.boardHeight)
  }
  // Decoration must never eat a click meant for the board.
  sprite.eventMode = 'none'
  stage.addChild(sprite)
}

export class PixiSlot {
  private app: Application | null = null
  /** Keeps the render resolution matched to the displayed size. */
  private sizeWatch: ResizeObserver | null = null
  /** Unregisters this app from the hidden-window clock. */
  private unclock: (() => void) | null = null
  private reels: Reel[] = []
  private pending: { final: string[][]; resolve: () => void } | null = null
  private T: WaysTiming = DEFAULT_WAYS_TIMING
  /** Win-line overlay (lines mechanic). Empty and invisible for ways games. */
  private lines: PixiLines | null = null
  /** Winning symbols mid-celebration: one spin-and-swell per lit line. */
  private celebrations: {
    art: Sprite
    baseW: number
    baseH: number
    elapsed: number
    duration: number
  }[] = []
  /** Iridescent "water" behind the winning cells. Redrawn every frame. */
  private winGlow: Graphics | null = null
  private glowCells: { reel: number; row: number; elapsed: number; duration: number }[] = []
  private glowTime = 0
  /**
   * Time since the fire geometry was last rebuilt. The tongues are re-drawn
   * at FIRE_FPS, not on every ticker frame: a full `Graphics.clear()` plus
   * ~30 beziers per lit cell is the most expensive thing on the board when
   * all 20 cells burn, and fire at 30 fps is indistinguishable from 60.
   */
  private glowSinceDraw = 0
  /**
   * One gradient per fire layer, built once and shared by every cell and
   * every frame. `textureSpace: 'local'` maps 0..1 onto the bounds of the
   * shape being filled, so the same object paints red-at-the-tips /
   * orange-at-the-root no matter how tall this cell's tongues stand.
   * Allocating a FillGradient per fill call was 60 canvas gradients and 60
   * texture uploads PER FRAME on a full-board win — the main jank source.
   */
  private fireFills: [FillGradient, FillGradient, FillGradient] | null = null
  private L: BoardLayout | null = null

  async init(host: HTMLElement, game: WaysGameView, theme?: ResolvedTheme): Promise<void> {
    this.T = theme?.timings.ways ?? DEFAULT_WAYS_TIMING
    const { tile: tileBase, max } = tileFor(WAYS_TILE, theme?.board)
    const L = boardLayout(game.layout.reels, game.layout.rows, tileBase, max)
    const palette = theme?.symbols ?? generatedPalette(game.symbols)
    const codes = game.symbols.map((s) => s.code)
    // Art loads before the reels are built: a tile created without its
    // texture would keep the placeholder until the next repaint.
    if (theme) await loadThemeAssets(theme)
    const ctx: ReelContext = {
      L,
      styleOf: (code) => styleFrom(palette, code),
      textureOf: (code) => (theme ? textureFor(theme, code) : null),
      backgroundOf: (code) => (theme ? backgroundTextureFor(theme, code) : null),
      animationOf: (code) => (theme ? animationFor(theme, code) : null),
      randomCode: () => codes[Math.floor(Math.random() * codes.length)] ?? '?',
      onReelStop: (i) => playReelStop(i),
      T: this.T,
      font: theme?.font ?? {
        family: 'Inter, Segoe UI, system-ui, sans-serif',
        weight: '800',
        letterSpacing: 1,
      },
      tileRadius: theme?.board.tileRadius ?? 14,
      theme: theme ?? null,
    }

    const app = new Application()
    await app.init({
      width: L.boardWidth,
      height: L.boardHeight,
      ...(theme?.board.background != null
        ? { background: theme.board.background, backgroundAlpha: 1 }
        : { backgroundAlpha: 0 }),
      antialias: true,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
    })

    host.appendChild(app.canvas)
    this.app = app
    // See backgroundClock: a hidden window stops rAF but not the round.
    this.unclock = driveWhileHidden(app)

    // The canvas renders at a fixed logical size and CSS shows it at
    // whatever the layout gives it — fullscreen most of all — so without
    // this the backing store is simply stretched and the art goes soft.
    //
    // The resolution is snapped UP to a whole multiple of the logical size.
    // A fractional one puts every reel on its own subpixel phase, and
    // identical symbols in one spin then come out looking different; a
    // whole multiple lands every cell on exact backing-store pixels and
    // leaves the browser one uniform downscale over the whole canvas.
    // The host is observed rather than the canvas: Pixi writes the canvas's
    // own style, and watching what you also write is how a resize loop starts.
    const matchResolution = (): void => {
      const shown = host.getBoundingClientRect().width
      if (shown <= 0) return
      const dpr = window.devicePixelRatio || 1
      const needed = Math.max(dpr, (shown / L.boardWidth) * dpr)
      const wanted = Math.min(3, Math.ceil(needed))
      if (Math.abs(wanted - app.renderer.resolution) < 0.05) return
      app.renderer.resize(L.boardWidth, L.boardHeight, wanted)
    }
    matchResolution()
    this.sizeWatch = new ResizeObserver(matchResolution)
    this.sizeWatch.observe(host)

    // Backdrop first, and outside the masked container: the mask exists to
    // hide buffer tiles, and a backdrop clipped by it would vanish.
    addArtLayer(app.stage, theme, BACKGROUND_KEY, L)

    const board = new Container()
    app.stage.addChild(board)
    this.L = L

    // The shimmer sits UNDER the reels: it tints the cell, never the art.
    this.winGlow = new Graphics()
    board.addChild(this.winGlow)

    for (let r = 0; r < L.reels; r++) {
      const reel = new Reel(r, ctx)
      this.reels.push(reel)
      board.addChild(reel.view)
    }

    // The mask hides the buffer tiles above and below the window
    const mask = new Graphics().rect(0, 0, L.boardWidth, L.boardHeight).fill(0xffffff)
    board.addChild(mask)
    board.mask = mask

    // Win lines sit above the reels and outside the mask (their glow may
    // spill past the cells), but under the frame art.
    this.lines = new PixiLines(L, theme?.timings.lines ?? DEFAULT_LINES_TIMING, (pos, dur) =>
      this.celebrate(pos, dur),
    )
    app.stage.addChild(this.lines.view)

    // Frame art last — it is drawn over the reels by definition.
    addArtLayer(app.stage, theme, FRAME_KEY, L)

    app.ticker.add((ticker) => this.update(ticker.deltaMS))
  }

  /** Show a window with no animation — the initial state, or a session restore. */
  showWindow(window: string[][]): void {
    this.reels.forEach((reel, i) => reel.setWindow(window[i]))
  }

  /**
   * Spin the reels and stop them on the given window.
   *
   * `speed` is a playback rate, not a switch: the reels ALWAYS scroll —
   * that motion is the game — a faster rate just gets them there sooner.
   * Turbo and losing rounds turn it up; they never skip the spin.
   */
  spinTo(window: string[][], opts?: { speed?: number }): Promise<void> {
    if (this.pending) return Promise.resolve()

    // A new spin wipes the previous round's win lines and celebrations.
    this.lines?.clear()
    for (const c of this.celebrations) {
      c.art.rotation = 0
      c.art.width = c.baseW
      c.art.height = c.baseH
    }
    this.celebrations = []
    this.glowCells = []
    this.glowSinceDraw = FIRE_FRAME_MS
    this.winGlow?.clear()

    const speed = Math.max(1, opts?.speed ?? 1)

    return new Promise<void>((resolve) => {
      this.pending = { final: window, resolve }
      this.reels.forEach((reel, i) => {
        reel.start(
          (i * this.T.reelStartStagger) / speed,
          (this.T.minSpinDuration + i * this.T.reelStopStagger) / speed,
          speed,
        )
      })
    })
  }

  /**
   * Lines mechanic: light the winning lines one after another, then all
   * together. Resolves when the sequence is over; the lines stay lit until
   * the next spin.
   */
  showLineWins(
    lineWins: readonly LineWinView[],
    opts?: { speed?: number; cycle?: boolean },
  ): Promise<void> {
    return this.lines?.play(lineWins, opts) ?? Promise.resolve()
  }

  /**
   * Winning symbols spin once around and swell while their line is lit.
   * Purely decorative and fire-and-forget: the promise chain of the round
   * is owned by PixiLines, this only rides the same clock.
   */
  private celebrate(
    positions: ReadonlyArray<{ reel: number; row: number }>,
    durationMs: number,
  ): void {
    for (const p of positions) {
      // The shimmer lights every winning cell, art or not.
      const glow = this.glowCells.find((g) => g.reel === p.reel && g.row === p.row)
      if (glow) {
        glow.elapsed = 0
        glow.duration = durationMs
      } else {
        this.glowCells.push({ reel: p.reel, row: p.row, elapsed: 0, duration: durationMs })
        // A freshly lit cell shows its fire on the very next frame.
        this.glowSinceDraw = FIRE_FRAME_MS
      }

      // Every drawn layer of the cell swells together.
      for (const art of this.reels[p.reel]?.artAt(p.row) ?? []) {
        // Restarting a running celebration (the same cell on two lines):
        // snap the sprite back first so base sizes stay honest.
        const existing = this.celebrations.find((c) => c.art === art)
        if (existing) {
          existing.elapsed = 0
          existing.duration = durationMs
          continue
        }
        this.celebrations.push({
          art,
          baseW: art.width,
          baseH: art.height,
          elapsed: 0,
          duration: durationMs,
        })
      }
    }
  }

  private updateCelebrations(dtMs: number): void {
    if (this.celebrations.length === 0) return
    for (const c of this.celebrations) {
      c.elapsed += dtMs
      const p = Math.min(1, c.elapsed / c.duration)
      // A gentle swell, nothing more. The card-flip is gone: the winning
      // cell now burns (see updateWinGlow), and a symbol that flickers on
      // top of a fire reads as two effects fighting for the same cell.
      const swell = 1 + 0.12 * Math.sin(Math.PI * p)
      c.art.width = c.baseW * swell
      c.art.height = c.baseH * swell
      if (p >= 1) {
        c.art.width = c.baseW
        c.art.height = c.baseH
      }
    }
    this.celebrations = this.celebrations.filter((c) => c.elapsed < c.duration)
  }

  /**
   * The fire behind winning cells: a SOLID blaze filling the whole cell,
   * up to its contour — the same recipe as the Blazing Seven's fireball.
   *
   * Three layers per cell, back to front: a red-tipped body covering the
   * cell, an orange middle, a yellow core. Each layer is one filled path —
   * straight sides hugging the cell edges, a wavy crown of tongues on top.
   * Tongue heights and tips ride their own sines, phase-shifted by cell
   * position, so the fire rolls across the combination instead of pumping
   * in unison. The symbol art sits above this layer and stays readable.
   */
  private updateWinGlow(dtMs: number): void {
    const glow = this.winGlow
    const L = this.L
    if (!glow || !L) return
    this.glowTime += dtMs / 1000

    if (this.glowCells.length === 0) return
    for (const c of this.glowCells) c.elapsed += dtMs
    this.glowSinceDraw += dtMs
    const finishedAny = this.glowCells.some((c) => c.elapsed >= c.duration)
    // Throttle the rebuild; always redraw when a cell just ended so its
    // fire is removed on time rather than a frame or two late.
    if (this.glowSinceDraw < FIRE_FRAME_MS && !finishedAny) return
    this.glowSinceDraw = 0

    if (!this.fireFills) {
      const make = (top: number, bottom: number): FillGradient => {
        const g = new FillGradient({
          start: { x: 0, y: 0 },
          end: { x: 0, y: 1 },
          textureSpace: 'local',
        })
        g.addColorStop(0, top)
        g.addColorStop(1, bottom)
        return g
      }
      // Colours are the art's own gradient stops (wFireOuter / wFireMid /
      // wFireInner), so the blaze behind a win and the fire inside WILD and
      // the seven are demonstrably the same fire.
      this.fireFills = [
        make(0xd92312, 0xff8a00),
        make(0xff7a00, 0xffc93c),
        make(0xffc93c, 0xfff8c8),
      ]
    }
    const fills = this.fireFills

    glow.clear()
    const t = this.glowTime
    for (const c of this.glowCells) {
      const p = Math.min(1, c.elapsed / c.duration)
      // Quick fade in/out with a full-brightness plateau in between — a
      // sine envelope kept the fire dim for most of its short life.
      const envelope = Math.min(1, 1.8 * Math.sin(Math.PI * p))
      if (envelope <= 0.02) continue
      const x = c.reel * L.pitch
      const y = c.row * L.pitch
      const size = L.symbolSize
      const cellPhase = (c.reel * 3 + c.row) * 0.9

      // The fire is built the way the theme's own art builds it (w.svg,
      // h1.svg): a bed of INDEPENDENT tongues, each a slender teardrop with
      // S-curved flanks that leans as it rises. The previous version drew one
      // polygon — a solid base with a serrated top edge — and a serrated edge
      // on a continuous baseline is a picket fence, which is exactly what it
      // looked like. Separate leaning shapes with gaps between them is what
      // makes the difference between fire and a fence.
      //
      // Proportions are lifted from the art: a tongue is roughly five times
      // taller than its base is wide, its control points swing out past the
      // base on the way up and cross back over on the way down.
      const yb = y + size - size * 0.06

      const tongues = (
        count: number,
        pad: number,
        hLo: number,
        hHi: number,
        fill: FillGradient,
        alpha: number,
        seed: number,
      ): void => {
        const x0 = x + size * pad
        const x1 = x + size - size * pad
        const step = (x1 - x0) / count
        const hw = step * 0.3
        for (let i = 0; i < count; i++) {
          const cx = x0 + step * (i + 0.5)
          const beat = t * 2.4 + i * 1.7 + cellPhase + seed
          // Standing height per tongue. An irrational stride keeps neighbours
          // from ever agreeing, so the row never combs itself even.
          const stand = 0.4 + 0.6 * Math.abs(Math.sin((i + 1) * 2.399 + cellPhase + seed))
          const h = size * (hLo + (hHi - hLo) * stand) * (0.86 + 0.14 * Math.sin(beat))
          // Alternating curl. All tongues leaning the same way rakes the row
          // into a comb, which is the fence again by another route.
          const dir = i % 2 === 0 ? 1 : -1
          const lean = dir * (hw * 0.9 + Math.sin(beat * 0.9) * hw * 0.7)
          glow.moveTo(cx + hw * dir, yb)
          glow.bezierCurveTo(
            cx + 2.2 * hw * dir,
            yb - h * 0.45,
            cx - 0.2 * hw * dir,
            yb - h * 0.65,
            cx + lean,
            yb - h,
          )
          glow.bezierCurveTo(
            cx - 2.6 * hw * dir,
            yb - h * 0.69,
            cx - 2.2 * hw * dir,
            yb - h * 0.25,
            cx - hw * dir,
            yb,
          )
          glow.closePath()
        }
        // One fill for the whole layer, exactly as the art does it with a
        // single <path> holding every tongue: red at the tips, orange at the
        // root. The gradient is shared (see `fireFills`) and spans the
        // layer's own bounds, so the tallest tongue sets the red end.
        glow.fill({ fill, alpha: alpha * envelope })
      }

      // The ember bed the tongues stand in. Without it they read as separate
      // objects hovering over the tile rather than as one fire.
      glow.ellipse(x + size / 2, yb, size * 0.4, size * 0.075)
      glow.fill({ color: 0xff6a00, alpha: 0.45 * envelope })

      // Heights follow w.svg: the outer body rises to roughly half the tile
      // behind the subject, the core stays low and bright.
      tongues(6, 0.05, 0.36, 0.56, fills[0], 0.95, 0)
      tongues(5, 0.13, 0.26, 0.42, fills[1], 0.95, 1.3)
      tongues(3, 0.24, 0.17, 0.29, fills[2], 1, 2.6)
    }
    const before = this.glowCells.length
    this.glowCells = this.glowCells.filter((c) => c.elapsed < c.duration)
    // The last cell finished this tick — leave a clean layer behind.
    if (before > 0 && this.glowCells.length === 0) glow.clear()
  }

  private update(dtMs: number): void {
    for (const reel of this.reels) reel.tickAnimations(dtMs)
    this.updateCelebrations(dtMs)
    this.updateWinGlow(dtMs)
    const pending = this.pending
    if (!pending) return

    for (let i = 0; i < this.reels.length; i++) {
      this.reels[i].update(dtMs, pending.final[i])
    }

    if (this.reels.every((r) => r.isIdle)) {
      this.pending = null
      pending.resolve()
    }
  }

  destroy(): void {
    // Cancel an unfinished spin, otherwise the promise never resolves and
    // the caller in React hangs forever.
    if (this.pending) {
      this.pending.resolve()
      this.pending = null
    }
    this.sizeWatch?.disconnect()
    this.sizeWatch = null
    this.lines?.destroy()
    this.lines = null
    this.unclock?.()
    this.unclock = null
    this.app?.destroy(true, { children: true })
    this.app = null
    this.reels = []
  }
}
